import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { Sparkle, Heart } from "./Decorations";

const FAQS = [
  {
    q: "What does a rheumatologist treat?",
    a: "A rheumatologist treats arthritis, autoimmune diseases, lupus, osteoporosis, inflammatory joint pain, and other conditions affecting the joints, muscles, bones, and immune system.",
  },
  {
    q: "When should I see a rheumatologist?",
    a: "You may consider seeing a rheumatologist if you have ongoing joint pain, swelling, stiffness, unexplained fatigue, autoimmune symptoms, or a family history of rheumatologic disease.",
  },
  {
    q: "Is this website medical advice?",
    a: "No. This website is for educational purposes only and does not replace medical care from your physician.",
  },
];

export default function FAQ() {
  // Use a Set so multiple FAQs can stay open. All items start closed by default.
  const [openSet, setOpenSet] = useState(() => new Set());

  const toggle = (i) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-28 bg-white overflow-hidden"
    >
      <Sparkle className="absolute top-20 right-12" color="#C7A5D9" size={18} />
      <Heart className="absolute bottom-16 left-12" color="#8E44AD" size={22} />

      <div className="max-w-4xl mx-auto px-5 lg:px-10">
        <div className="text-center">
          <p className="pill mb-5 reveal">Common Questions</p>
          <h2
            className="h-display text-3xl sm:text-4xl title-underline reveal"
            data-testid="faq-title"
          >
            Rheumatology FAQ
          </h2>
        </div>

        <div className="mt-12 space-y-4" data-testid="faq-list">
          {FAQS.map((f, i) => {
            const isOpen = openSet.has(i);
            return (
              <div
                key={f.q}
                data-testid={`faq-item-${i}`}
                className={`card-soft p-0 overflow-hidden ${isOpen ? "border-royal/60" : ""}`}
                style={{ animation: `faqIn .7s ${i * 90}ms both` }}
              >
                <button
                  onClick={() => toggle(i)}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full flex items-center justify-between gap-6 text-left px-6 py-5"
                  aria-expanded={isOpen}
                >
                  <span className="font-display text-[14px] tracking-[0.18em] uppercase text-plum">
                    {f.q}
                  </span>
                  <span
                    className={`h-9 w-9 rounded-full bg-lavender-bg text-royal grid place-items-center flex-shrink-0 transition-transform duration-500 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  >
                    {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                  </span>
                </button>
                <div
                  className={`grid transition-all duration-500 ease-out ${
                    isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="px-6 pb-6 text-[15px] leading-relaxed text-charcoal/80">
                      {f.a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes faqIn {
          from { opacity: 0; transform: translateY(20px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </section>
  );
}
